import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
 

const services = [
  { icon: 'fa-brands fa-apple', title: '1. iOS App Development', desc: 'Build high-performance native iOS applications tailored for iPhone and iPad using Swift.' },
  { icon: 'fa-brands fa-android', title: '2. Android App Development', desc: 'Develop robust native Android applications engineered with Kotlin for high performance and smooth execution.' },
  { icon: 'fa-solid fa-arrows-split-up-and-left', title: '3. Cross-Platform App Development', desc: 'Create seamless cross-platform apps using React Native and Flutter to deliver native performance across iOS and Android.' },
  { icon: 'fa-solid fa-wand-magic-sparkles', title: '4. Mobile App UI/UX Design', desc: 'Design intuitive, engaging, and touch-optimized mobile interfaces that deliver frictionless user journeys.' },
  { icon: 'fa-solid fa-globe', title: '5. Progressive Web Apps (PWA)', desc: 'Build reliable, installable web apps that deliver native-like mobile experiences directly through web browsers.' },
  { icon: 'fa-solid fa-building-user', title: '6. Enterprise Mobile Solutions', desc: 'Custom enterprise mobility software designed to digitize internal workflows and boost operational productivity.' },
  { icon: 'fa-solid fa-cloud-arrow-up', title: '7. Mobile Backend & API Integration', desc: 'Develop secure backend architectures and APIs to power real-time data sync, push notifications, and payments.' },
  { icon: 'fa-solid fa-vial-circle-check', title: '8. Mobile App Testing & QA', desc: 'Conduct multi-device QA testing across multiple OS versions and network environments for bug-free deployment.' },
  { icon: 'fa-solid fa-chart-line', title: '9. App Store Optimization (ASO)', desc: 'Optimize your mobile application listing to maximize organic downloads on Apple App Store & Google Play.' },
  { icon: 'fa-solid fa-screwdriver-wrench', title: '10. App Maintenance & Support', desc: 'Keep your app secure and up-to-date with active monitoring, OS updates, and bug fixes.' },
];

const whyChooseUs = [
  'Native & Cross-Platform expertise',
  'User-centric mobile UI/UX design',
  'Robust data security & encryption',
  'High performance & smooth 60fps UI',
  'Agile development methodology',
  'Seamless backend & API integration',
  'App Store & Play Store publishing support',
  'Scalable architecture for growing users',
  'End-to-end maintenance & version updates',
  'Dedicated QA & real device testing',
];

const processSteps = [
  { step: '01', title: 'Strategy', desc: 'Define target users, core features, technology stack, and business objectives.' },
  { step: '02', title: 'Wireframing', desc: 'Map out app navigation, screen flows, user interactions, and visual layouts.' },
  { step: '03', title: 'UI/UX Design', desc: 'Craft sleek, modern interfaces with brand consistency and pixel-perfect design.' },
  { step: '04', title: 'Development', desc: 'Code backend APIs, integrate databases, and build fluid mobile interfaces.' },
  { step: '05', title: 'QA Testing', desc: 'Run performance, security, usability, and multi-device compatibility tests.' },
  { step: '06', title: 'Publishing', desc: 'Handle Apple App Store and Google Play Store submission procedures.' },
  { step: '07', title: 'Maintenance', desc: 'Provide continuous app updates, OS compatibility patches, and feature additions.' },
];

const techStack = [
  { category: 'Mobile Frameworks', items: ['React Native', 'Flutter', 'Swift', 'Kotlin'] },
  { category: 'Backend & Cloud', items: ['Node.js', 'Firebase', 'AWS Mobile Hub', 'GraphQL', 'REST APIs'] },
  { category: 'Database', items: ['SQLite', 'Realm', 'MongoDB', 'PostgreSQL'] },
  { category: 'Deployment & CI/CD', items: ['App Store Connect', 'Google Play Console', 'Fastlane', 'Bitrise'] },
];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.12 },
  },
};

const cardVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: 'easeOut' } },
};

const MobileAppDevelopment = () => {
  return (
    <div className="service-page text-white overflow-hidden">
      {/* HERO SECTION */}
      <section className="hero-sub-section text-center d-flex align-items-center justify-content-center">
        <div className="container py-5">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="badge rounded-pill bg-outline-cyan px-3 py-2 mb-3 glow-badge">
              Lidlbritain App Solutions
            </span>
            <h1 className="display-4 fw-bold mb-3">
              Mobile App <span className="gradient-text-cyan">Development</span>
            </h1>
            <p className="lead mx-auto opacity-75 max-width-800">
              At <strong>Lidlbritain</strong>, we design and build high-performance mobile applications that engage users, scale effortlessly, and fuel business growth across iOS and Android.
            </p>
            <div className="mt-4">
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} className="d-inline-block">
                <Link to="/contact" className="btn gradient-btn px-4 py-2 rounded-pill">
                  Build Your App With Lidlbritain &rarr;
                </Link>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* SERVICES SECTION */}
      <section className="py-5">
        <div className="container">
          <div className="text-center mb-5">
            <h2 className="fw-bold">
              Mobile Engineering by <span className="gradient-text-orange">Lidlbritain</span>
            </h2>
            <div className="title-line mx-auto mt-2"></div>
          </div>

          <motion.div
            className="row g-4"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-50px' }}
          >
            {services.map((item, index) => (
              <div key={index} className="col-md-6 col-lg-4">
                <motion.div
                  variants={cardVariants}
                  whileHover={{ y: -8, transition: { duration: 0.2 } }}
                  className="service-card p-4 rounded-4 h-100 border border-secondary border-opacity-25 bg-glass"
                >
                  <div className="icon-box mb-3 d-flex align-items-center justify-content-center floating-icon">
                    <i className={`${item.icon} fs-3 text-cyan`}></i>
                  </div>
                  <h5 className="fw-bold mb-3">{item.title}</h5>
                  <p className="text-light opacity-75 small leading-relaxed">{item.desc}</p>
                </motion.div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* WHY CHOOSE US */}
      <section className="py-5 bg-dark-overlay">
        <div className="container">
          <div className="row g-5 align-items-center">
            <motion.div
              className="col-lg-6"
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h3 className="fw-bold mb-4">
                Why Choose <span className="gradient-text-cyan">Lidlbritain?</span>
              </h3>
              <div className="row g-3">
                {whyChooseUs.map((reason, idx) => (
                  <div key={idx} className="col-sm-6">
                    <div className="d-flex align-items-center gap-2">
                      <i className="fa-solid fa-circle-check text-orange pulse-icon"></i>
                      <span className="small opacity-90">{reason}</span>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div
              className="col-lg-6"
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="p-4 rounded-4 border border-secondary border-opacity-25 bg-glass glow-box">
                <h4 className="fw-bold mb-4">Mobile Tech Stack</h4>
                {techStack.map((tech, idx) => (
                  <div key={idx} className="mb-3">
                    <h6 className="text-cyan mb-2 fw-semibold">{tech.category}</h6>
                    <div className="d-flex flex-wrap gap-2">
                      {tech.items.map((item, i) => (
                        <motion.span
                          whileHover={{ scale: 1.1 }}
                          key={i}
                          className="badge bg-secondary bg-opacity-22 border border-secondary border-opacity-50 text-light fw-normal py-2 px-3"
                        >
                          {item}
                        </motion.span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="py-5">
        <div className="container">
          <div className="text-center mb-5">
            <h2 className="fw-bold">
              The Lidlbritain <span className="gradient-text-orange">App Framework</span>
            </h2>
            <div className="title-line mx-auto mt-2"></div>
          </div>

          <motion.div
            className="row g-4"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {processSteps.map((stepItem, index) => (
              <div key={index} className="col-md-6 col-lg-4 col-xl-3">
                <motion.div
                  variants={cardVariants}
                  whileHover={{ scale: 1.03 }}
                  className="process-card p-4 rounded-4 h-100 bg-glass border border-secondary border-opacity-25 position-relative"
                >
                  <span className="step-number fw-bold text-cyan mb-2 d-block">{stepItem.step}</span>
                  <h5 className="fw-bold mb-2">{stepItem.title}</h5>
                  <p className="text-light opacity-75 small mb-0">{stepItem.desc}</p>
                </motion.div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* CALL TO ACTION */}
      <section className="py-5 my-4">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="p-5 rounded-5 cta-banner text-center position-relative overflow-hidden"
          >
            <h2 className="fw-bold mb-3">Launch Your Next App with Lidlbritain</h2>
            <p className="lead opacity-90 mx-auto mb-4 max-width-800">
              Transform your concept into a top-rated, feature-packed app. Work directly with Lidlbritain app development engineers.
            </p>
            <motion.div whileHover={{ scale: 1.08 }} whileTap={{ scale: 0.95 }} className="d-inline-block">
              <Link to="/contact" className="btn gradient-btn btn-lg px-5 py-3 rounded-pill fw-semibold">
                Start Mobile App Project &rarr;
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default MobileAppDevelopment;